// ─── I giorni in cui non ci sono ─────────────────────────────────────────────
//
// 🗣️⭐⭐ **LA DECISIONE È DI SEI PAROLE**, del 19/09/2026, alla domanda su come
// un maestro che non c'è — ferie, malattia — possa dirlo: **«sì, si blocca le
// ore da sé»**. ⇒ Questa è l'unica metà del perimetro che scrive **lui**: le
// ore le apre la segreteria, e di là si leggono e basta.
//
// ⚖️ **PERCHÉ STA DENTRO «QUANDO PUOI PRENOTARE» E NON IN UNA SCHERMATA SUA.**
// Il perimetro ha due metà scritte da due mani, e leggerle separate vorrebbe
// dire guardare un elenco di ore senza sapere che tre di quei giorni sono già
// tolti. ⇒ Una schermata sola sulle proprie ore, raggiunta dalla riga in fondo
// all'agenda (dal 23/09, voce 13, la barra in basso non c'è più).
// 📌 *Un divieto e la cosa che vieta si leggono insieme, o il divieto sembra
//    arbitrario e la cosa sembra intera.*
//
// ⛔⛔ **E QUESTA SCHERMATA NON ANNULLA NIENTE**, che è la cosa da sapere prima
//    di tutto il resto: le lezioni e le volte di corso che cadono dentro
//    l'assenza **restano in agenda**, con dentro persone che ci contano. ⇒ La
//    cura non è fare di più: è **dirlo** — ogni riga porta quante volte cadono
//    lì dentro, e lo dice nel momento in cui la si scrive.
// 📌 *Un gesto che non fa una cosa deve dirlo proprio a chi crede di averla
//    fatta.*

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AssenzaDelMaestro } from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { CampoData } from '../../components/CampoData.js';
import { Attesa, Guasto } from '../../components/pezzi.js';
import { giornoDellaSettimana, giornoSenzaAnno } from '../../lib/time.js';

interface MieAssenze {
  items: AssenzaDelMaestro[];
}

const CHIAVE = ['maestro', 'assenze'];

/**
 * 🗓️ **Il periodo come lo direbbe qualcuno: «Da lun 21 a ven 25 settembre».**
 *
 * ⚖️ Il mese si scrive **una volta sola** quando i due giorni ci stanno dentro:
 * «da lun 21 settembre a ven 25 settembre» è la stessa cosa detta due volte, e
 * a 320 px va a capo nel mezzo. Quando invece il periodo scavalca il mese, i
 * due mesi ci sono tutti e due — lì servono.
 *
 * ⭐ Un giorno solo non si scrive come un periodo di uno: si scrive **il
 *    giorno**. «Da ven 25 a ven 25 settembre» fa fermare chi legge.
 */
export function periodoScritto(dalGiorno: string, alGiorno: string): string {
  const breve = (d: string): string => giornoDellaSettimana(d).slice(0, 3);
  if (dalGiorno === alGiorno) return `${breve(dalGiorno)} ${giornoSenzaAnno(dalGiorno)}`;
  const stessoMese = dalGiorno.slice(0, 7) === alGiorno.slice(0, 7);
  const primo = stessoMese
    ? `${breve(dalGiorno)} ${Number(dalGiorno.slice(8, 10))}`
    : `${breve(dalGiorno)} ${giornoSenzaAnno(dalGiorno)}`;
  return `Da ${primo} a ${breve(alGiorno)} ${giornoSenzaAnno(alGiorno)}`;
}

/**
 * Quanti giorni sono, **estremi compresi**.
 *
 * 🗓️ Il `+ 1` è la regola di questa tappa scritta in aritmetica: «dal 21 al 25»
 * sono **cinque** giorni. Senza, un maestro leggerebbe «4 giorni» su un'assenza
 * che ne copre cinque — e l'errore di un giorno solo è quello che nessuno nota.
 */
export function quantiGiorni(dalGiorno: string, alGiorno: string): number {
  return Math.round((Date.parse(alGiorno) - Date.parse(dalGiorno)) / 86_400_000) + 1;
}

export function QuandoNonCiSono(): React.JSX.Element {
  const coda = useQueryClient();

  const [dal, setDal] = useState('');
  const [al, setAl] = useState('');

  const assenze = useQuery({
    queryKey: CHIAVE,
    queryFn: () => chiama<MieAssenze>('GET', '/maestro/assenze'),
  });

  /**
   * 🔁 **Cosa si rinfresca dopo, e perché non è «tutto».** Un'assenza cambia il
   * **perimetro**, quindi l'elenco degli spazi liberi mente finché non lo si
   * rilegge (e la risposta vecchia resta buona per trenta secondi, ⇒
   * `main.tsx`). ⛔ L'agenda e i soldi **non cambiano**: le volte già prenotate
   * restano tutte, ed è il punto di questa tappa.
   * ⭐ L'elenco nuovo arriva **dalla risposta stessa** e si scrive in cassa: è
   *    già quello giusto, e chiederlo di nuovo sarebbe un giro di rete per
   *    sapere una cosa che si ha in mano.
   */
  function atterrata(dati: MieAssenze): void {
    coda.setQueryData(CHIAVE, dati);
    void coda.invalidateQueries({ queryKey: ['maestro', 'spazi'] });
  }

  const scrivo = useMutation({
    meta: { fatto: 'assenza segnata' },
    mutationFn: () => chiama<MieAssenze>('POST', '/maestro/assenze', { dalGiorno: dal, alGiorno: al }),
    onSuccess: (dati) => {
      atterrata(dati);
      /* ⭐ I campi si svuotano **quando è andata**, non quando si è toccato: se
         il server rifiuta — sovrapposta, già finita — le due date restano sotto
         il messaggio che le riguarda, pronte da correggere. */
      setDal('');
      setAl('');
    },
  });

  const torno = useMutation({
    meta: { fatto: 'assenza tolta' },
    mutationFn: (id: string) => chiama<MieAssenze>('DELETE', `/maestro/assenze/${id}`),
    onSuccess: atterrata,
  });

  const inCorso = scrivo.isPending || torno.isPending;
  const items = assenze.data?.items ?? [];

  return (
    <section className="riquadro">
      <h2>Quando non ci sono</h2>

      {assenze.isLoading ? (
        <Attesa cosa="i tuoi giorni" />
      ) : assenze.error ? (
        <Guasto errore={assenze.error} />
      ) : items.length === 0 ? (
        <>
          <p className="tenue">Non hai scritto nessun giorno.</p>
          <p className="tenue piccolo">
            Qui dici quando non ci sei: in quei giorni il gestionale smette di proporti delle ore.
          </p>
        </>
      ) : (
        <>
          <ul className="giorni-via">
            {items.map((a) => (
              <li key={a.id}>
                <span>
                  <span className="quando">{periodoScritto(a.dalGiorno, a.alGiorno)}</span>
                  <span className="quanto">
                    {quantiGiorni(a.dalGiorno, a.alGiorno)}
                    {quantiGiorni(a.dalGiorno, a.alGiorno) === 1 ? ' giorno' : ' giorni'}
                    {a.volte > 0 && ` · ${a.volte} ${a.volte === 1 ? 'volta' : 'volte'} già in campo`}
                  </span>
                </span>
                {/* ⭐ «Ci sono», non «Togli»: è la frase opposta a quella che ha
                    scritto la riga, e dice cosa diventa vero — non cosa sparisce
                    dall'elenco. */}
                <button
                  type="button"
                  className="gesto-riga"
                  disabled={inCorso}
                  onClick={() => torno.mutate(a.id)}
                >
                  Ci sono
                </button>
              </li>
            ))}
          </ul>
          {/* ⛔⭐ La riga che dice cosa questa schermata NON ha fatto. Compare
              solo quando c'è qualcosa in campo lì dentro: dirlo sempre sarebbe
              spiegare un pericolo che non c'è. */}
          {items.some((a) => a.volte > 0) && (
            <p className="regola">
              Le volte che hai già in campo in quei giorni <strong>restano dove sono</strong>: se non le fai,
              disdicile una per una.
            </p>
          )}
        </>
      )}

      <div className="scrivi-via">
        <div className="due-date">
          {/*
            🚨🔄⭐⭐ **LE DATE LE SCRIVIAMO NOI, e il mockup diceva il contrario.**
            Il disegno approvato portava `type="date"` — *«il calendario è quello
            del telefono»* — e quella riga contraddiceva una decisione **sua**
            più vecchia e già misurata: 🗣️ *«Sì, la data scrivila tu in
            italiano»* (17/09/2026), davanti a un campo che diceva `04/22/2027`.
            ⚖️ Quel formato non lo governa niente di questo repository: lo
               disegna il browser con la **sua** lingua, non con quella della
               pagina. ⇒ Si usa `CampoData`, che è già la risposta a quella
               domanda: testo `gg/mm/aaaa` scritto da noi, e il calendarietto
               del telefono **sotto**, al servizio del bottoncino.
            📌 *Il gesto approvato era «due date»: quale pezzo le disegna non è
               una scelta di questa schermata, è una decisione già presa altrove.*
          */}
          <CampoData
            etichetta="Dal giorno"
            value={dal}
            disabled={inCorso}
            onChange={(iso) => {
              setDal(iso);
              /* ⭐ Un giorno solo è il caso più comune — ci si ammala di
                 giovedì — e chiederlo due volte sarebbe far battere la stessa
                 data due volte. ⚠️ Si tocca il secondo campo **solo** se è
                 vuoto o se è rimasto indietro: una data già scritta a mano non
                 si sovrascrive. */
              if (iso !== '' && (al === '' || al < iso)) setAl(iso);
            }}
          />
          {/* 🗓️ «compreso» è la parola che evita l'errore di un giorno solo:
              «dal 21 al 25» sono cinque giorni, il 25 dentro. */}
          <CampoData
            etichetta="Al giorno, compreso"
            value={al}
            min={dal === '' ? undefined : dal}
            disabled={inCorso}
            onChange={setAl}
          />
        </div>
        <button type="button" disabled={inCorso || dal === '' || al === ''} onClick={() => scrivo.mutate()}>
          {dal !== '' && al !== ''
            ? `Non ci sono ${periodoScritto(dal, al).replace(/^Da /, 'da ')}`
            : 'Non ci sono in questi giorni'}
        </button>
      </div>

      {/* ⚠️ Il rifiuto si legge QUI, sotto il bottone che si è premuto: in cima
          alla pagina, su un telefono, starebbe fuori schermo. */}
      <Guasto errore={scrivo.error ?? torno.error} />
    </section>
  );
}

/**
 * 🏖️⭐ **«UN GIORNO IN CUI NON CI SONO» — una pagina sua** (voce 16, 23/09/2026).
 *
 * 🗣️ Guardando l'anteprima: *«quando clicco sul bottone un giorno in cui non
 * ci sono, si deve aprire una pagina con le date in cui io non ci sono e poi la
 * possibilità di inserire i giorni di assenza»*.
 * ⇒ Prima la scelta del «+» apriva la pagina delle ore («Quando puoi
 *   prenotare») e la faceva scorrere fino alle assenze: la cosa cercata stava in
 *   fondo a un'altra. Adesso è la pagina intera — in cima i giorni già scritti,
 *   sotto il modo di aggiungerne — ed è la stessa sezione di prima, non una
 *   copia: `QuandoNonCiSono` ha già quell'ordine.
 */
export function PaginaAssenze(): React.JSX.Element {
  return (
    <>
      <p>
        <Link className="indietro" to="/maestro">
          ‹ La mia agenda
        </Link>
      </p>
      <QuandoNonCiSono />
    </>
  );
}
